import { useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useEventStream } from '../../hooks/useEventStream.js';

interface FeedEntry {
  id: string;
  icon: string;
  iconColor: string;
  message: string;
  href: string | null;
  ts: number;
}

function timeAgo(ts: number): string {
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
}

let idSeq = 0;

export function LiveFeed() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<FeedEntry[]>([]);

  const push = useCallback((entry: Omit<FeedEntry, 'id' | 'ts'>) => {
    setEntries((prev) => {
      const next = [{ ...entry, id: String(idSeq++), ts: Date.now() }, ...prev];
      return next.slice(0, 50);
    });
  }, []);

  const handleEvent = useCallback((msg: MessageEvent) => {
    try {
      const event = JSON.parse(msg.data as string) as {
        type: string;
        payload: Record<string, unknown>;
      };
      const p = event.payload as Record<string, unknown>;

      if (event.type === 'stage.finished') {
        const stage = p.stage as string;
        const status = p.status as string;
        const sessionId = p.sessionId as string;
        if (status === 'done' && stage === 'export') {
          push({ icon: '✓', iconColor: 'var(--accent-green)', message: `Export done · session ${sessionId.slice(0, 8)}`, href: `/sessions/${sessionId}` });
        } else if (status === 'error') {
          push({ icon: '✕', iconColor: 'var(--accent-red)', message: `Stage error: ${stage} · session ${sessionId.slice(0, 8)}`, href: `/sessions/${sessionId}` });
        }
      }
      if (event.type === 'stage.started') {
        const stage = p.stage as string;
        const sessionId = p.sessionId as string;
        if (stage === 'scrape') {
          push({ icon: '◉', iconColor: 'var(--accent-blue)', message: `Session started · ${sessionId.slice(0, 8)}`, href: `/sessions/${sessionId}` });
        }
      }
      if (event.type === 'conflict') {
        const sessionId = p.sessionId as string;
        const kind = p.kind as string;
        push({ icon: '⚠', iconColor: 'var(--accent-yellow)', message: `Conflict: ${kind} · session ${sessionId.slice(0, 8)}`, href: '/conflicts' });
      }
      if (event.type === 'recipe.updated') {
        const ehrSlug = p.ehrSlug as string;
        push({ icon: '◉', iconColor: 'var(--accent-teal)', message: `Recipe updated · ${ehrSlug}`, href: '/ehrs' });
      }
    } catch { /* ignore */ }
  }, [push]);

  useEventStream({
    topics: ['ehr:catalog', 'build:catalog', 'session:all'],
    onEvent: handleEvent,
  });

  return (
    <div className="glass rounded-2xl flex flex-col overflow-hidden h-full" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
      <div className="px-4 py-3 border-b border-border">
        <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary">Live activity</div>
      </div>
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: '460px' }}>
        {entries.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-text-dimmed">
            Waiting for events…
          </div>
        ) : (
          <div className="divide-y divide-border">
            {entries.map((e) => (
              <div
                key={e.id}
                className="feed-entry flex items-start gap-2.5 px-4 py-2.5 hover:bg-bg-tertiary transition-colors"
                role={e.href ? 'button' : undefined}
                onClick={e.href ? () => void navigate({ to: e.href as '/' }) : undefined}
                style={{ cursor: e.href ? 'pointer' : 'default' }}
              >
                <span className="text-sm shrink-0 mt-0.5" style={{ color: e.iconColor }}>{e.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-text-primary leading-snug">{e.message}</div>
                  <div className="text-[10px] text-text-dimmed mt-0.5">{timeAgo(e.ts)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
